import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Items } from "assets/SideBarIcons/items.svg";
import StyledInput from "common/SearchField/Search";
import { useAssignItemPriceListService, useGetItemPriceListService, useUnassignItemPriceListService } from 'subModule/src/services/items/useItems';
import ItemTabs from '../ItemTabs';
import { Grid } from '@mui/material';
import { ItemsPriceListTable } from './ItemsPriceListTable';
import Loader from 'layout/Loader';
import { useForm } from 'react-hook-form';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import { selectUserInfo } from 'features/user/userSlice';
import { useSelector } from 'react-redux';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';
import { ItemsProfileService } from 'subModule/src/core/services';

export default function ItemsPriceListListing() {
    const params = useParams();
    const itemId = params?.id;
    const navigate = useNavigate();
    const [itemTitle, setItemTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<any>('');
    const [searchTerm, setSearchTerm] = useState<any>('');
    const { register, control, formState: { errors }, watch, setValue, trigger, reset } = useForm<FormData>({});
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetItemPriceListService({ pageNumber: pageNo, size: pageSize, title: searchTerm }, +(params as any).id) : useGetItemPriceListService({ pageNumber: pageNo, size: pageSize }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!itemId) return;
        let active = true;
        ItemsProfileService.getItemByID(+itemId)
            .then((res: unknown) => {
                if (!active) return;
                const name = pickProfileEntityTitle(res);
                if (name) setItemTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [itemId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'items', href: '/parts' },
            {
                labelKey: 'title',
                ...(itemTitle ? { label: itemTitle } : {}),
                href: itemId ? `/part/${itemId}` : undefined,
            },
            { labelKey: 'assignPriceList' },
        ],
        [itemId, itemTitle],
    );

    const loggedInUserData = useSelector(selectUserInfo);
    const defaultPriceListId = loggedInUserData?.business?.priceListId || 0;
    const columns = [
        { id: 'pricelistId', label: '', numeric: false, disablePadding: false },
        { id: 'title', label: t('title'), numeric: false },
        { id: 'price', label: t('price'), numeric: false },
    ];
    const fetchData = useCallback(() => {
        reset();
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, searchTerm]);
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') handleSearch(trimmedSearch);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onUnassignPriceList({ itemId: +(params as any).id, pricelistId: row.id });
        } else {
            onAssignPriceList({ itemId: +(params as any).id, pricelistId: row.id, price: row.price });
        }
    }
    const { onAssignPriceList } = useAssignItemPriceListService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('priceList') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignPriceList } = useUnassignItemPriceListService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('priceList') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/parts')}
                heading={t('items')}
                icon={<Items height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <ItemTabs params={params} selectedIndex={4} /> : null}
                    <Box className='w-[100%]'>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7} lg={5} className='mt-3'>
                                <StyledInput
                                    fullWidth
                                    placeholder={t("title")}
                                    handleSearchClick={handleSearchClick}
                                    handleChange={handleInputChange}
                                    value={search}
                                    clearSearch={clearSearch}
                                />
                            </Grid>
                        </Grid>
                        {(isFetching || loading) ? <Loader /> : <ItemsPriceListTable
                            columns={columns}
                            rows={tableData?.pages[0]?.data?.content || []}
                            assignUnassignSingleEntity={assignUnassignSingleEntity}
                            form={{ register, control, formState: { errors }, watch, setValue, trigger, reset }}
                            assignPrivilege={checkPrivileges('ROLE_ITEM_PRICELIST_ASSIGN')}
                            unassignPrivilege={checkPrivileges('ROLE_ITEM_PRICELIST_UNASSIGN')}
                            {...{pageNo, pageSize, defaultPriceListId}}
                        />}
                    </Box>
                </div>
            </Box>
        </Box>
    )
}